import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { SealCheckIcon, ArrowRightIcon, ArrowSquareOutIcon, StarIcon } from "@phosphor-icons/react";
import { fetchProvenBuilders, fetchBuilderFollows } from "../lib/api";
import { scoreColor } from "../lib/format";
import { verdictColor } from "./DegenScore";
import { getClientId } from "../lib/clientId";

const age = (m) => {
  if (m == null) return "";
  if (m < 60) return `${Math.round(m)}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
};

export const ProvenBuilders = () => {
  const cid = getClientId();
  const { data: followData } = useQuery({
    queryKey: ["builderFollows", cid],
    queryFn: () => fetchBuilderFollows(cid),
    staleTime: 30000,
  });
  const follow = (followData?.creators || []).join(",");

  const { data } = useQuery({
    queryKey: ["provenBuilders", follow],
    queryFn: () => fetchProvenBuilders(12, follow),
    refetchInterval: 120000,
  });

  const builders = data?.builders || [];

  return (
    <div className="border border-neon/25 bg-gradient-to-br from-neon/[0.06] to-transparent p-5" data-testid="proven-builders">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <SealCheckIcon size={20} weight="fill" className="text-neon" /> Proven Builders
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Live coins from deployers with a real runner track record ($1M+ / $5M+ past coins). Follow a builder to pin them here.
          </p>
        </div>
        <Link to="/leaderboard" data-testid="proven-builders-all" className="hidden sm:flex items-center gap-1 text-xs font-mono text-neon hover:underline shrink-0">
          Top deployers <ArrowRightIcon size={13} />
        </Link>
      </div>

      {builders.length === 0 ? (
        <div className="text-sm text-muted-foreground font-mono py-6 text-center" data-testid="proven-builders-empty">
          Watching for the next proven-builder launch — new coins from top deployers appear here the moment they land.
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
          {builders.map((b) => {
            const isLaunch = b.source === "launch";
            const inner = (
              <>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-heading font-bold text-sm truncate flex items-center gap-1">
                    {b.followed && <StarIcon size={12} weight="fill" className="text-neon shrink-0" />}
                    ${(b.symbol || "?").replace("$", "")}
                  </span>
                  {isLaunch ? (
                    <span className="text-[9px] font-mono px-1.5 py-0.5 bg-neon/15 text-neon border border-neon/30 shrink-0">🆕 FRESH</span>
                  ) : b.score != null ? (
                    <span className="font-mono font-bold text-sm shrink-0" style={{ color: scoreColor(b.score) }}>{b.score}</span>
                  ) : null}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{b.name}</div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-mono px-1.5 py-0.5 border border-neon/30 text-neon bg-neon/[0.05]">
                    ⭐ {[b.strongRunnerCount > 0 && `${b.strongRunnerCount}×$5M`, b.runnerCount > 0 && `${b.runnerCount}×$1M`].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-muted-foreground/70">
                  <span>{age(b.ageMinutes)} old</span>
                  {isLaunch ? (
                    <span className="flex items-center gap-0.5 text-neon">pump.fun <ArrowSquareOutIcon size={10} /></span>
                  ) : (
                    <span style={{ color: verdictColor(b.verdict) }}>{b.verdict}</span>
                  )}
                </div>
              </>
            );
            const cls = `shrink-0 w-[180px] snap-start bg-black/40 border p-3 transition-colors ${
              isLaunch ? "border-neon/40 animate-pulse-fresh" : b.followed ? "border-neon/40 hover:border-neon/60" : "border-white/10 hover:border-neon/40"
            }`;
            return isLaunch ? (
              <a key={b.mint} href={`https://pump.fun/${b.mint}`} target="_blank" rel="noreferrer" data-testid={`proven-builder-card-${b.mint}`} className={cls}>
                {inner}
              </a>
            ) : (
              <Link key={b.mint} to={`/coin/${b.mint}`} data-testid={`proven-builder-card-${b.mint}`} className={cls}>
                {inner}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
